import { useState } from 'react';
import { line, Size, t } from '@bms2/shared';
import { Button, Select } from '../../components/controls';
import { AlertBanner } from '../../components/controls/AlertBanner';
import { Modal } from '../../components/modals/Modal';
import modalStyles from '../../components/modals/modal.module.css';
import { CATEGORIES_PATH } from '../settings/api';
import { patch, post, useRows } from './api';
import styles from './RfqModal.module.css';

/** 🔨 Relative to the API's own root, never prefixed with /api. */
export const VENDOR_REPS_PATH = '/vendor-reps';

export interface VendorRepRow {
  id: string;
  vendorCompanyId: string;
  itemCategoryId: string;
  categoryName: string;
  categoryPath: string;
  isActive: boolean;
}

interface CategoryRow {
  id: string;
  name: string;
  parentId: string | null;
}

/** The tree flattened for a select, each row indented to its depth, in the order the settings tree lists them. */
export function categoryOptions(rows: readonly CategoryRow[]): { id: string; label: string }[] {
  const out: { id: string; label: string }[] = [];
  const walk = (parentId: string | null, depth: number) => {
    for (const row of rows.filter((r) => r.parentId === parentId)) {
      out.push({ id: row.id, label: `${'  '.repeat(depth)}${row.name}` });
      walk(row.id, depth + 1);
    }
  };
  walk(null, 0);
  return out;
}

export interface VendorRepModalProps {
  open: boolean;
  vendorCompanyId: string;
  apiBaseUrl?: string;
  onCancel: () => void;
  onChanged: () => void;
}

/**
 * The categories a vendor represents (piece 22; decision 0158): undated rows,
 * added by name and switched off rather than deleted. Repping a category reps
 * every category beneath it, which is what the RFQ's picker reads.
 */
export function VendorRepModal({ open, vendorCompanyId, apiBaseUrl = '', onCancel, onChanged }: VendorRepModalProps) {
  const reps = useRows<VendorRepRow>(open ? `${VENDOR_REPS_PATH}?vendorCompanyId=${encodeURIComponent(vendorCompanyId)}` : null, apiBaseUrl);
  const categories = useRows<CategoryRow>(open ? CATEGORIES_PATH : null, apiBaseUrl);
  const [chosen, setChosen] = useState('');
  const [error, setError] = useState<string | null>(null);
  const rows = reps.state.kind === 'ready' ? reps.state.data : [];
  const options = categoryOptions(categories.state.kind === 'ready' ? categories.state.data : []);

  const add = async () => {
    if (!chosen) return;
    const answer = await post<VendorRepRow>(VENDOR_REPS_PATH, { vendorCompanyId, itemCategoryId: chosen }, apiBaseUrl);
    if (!answer.ok) return setError(answer.message);
    setChosen('');
    reps.reload();
    onChanged();
  };

  const toggle = async (row: VendorRepRow) => {
    const answer = await patch<VendorRepRow>(`${VENDOR_REPS_PATH}/${row.id}`, { isActive: !row.isActive }, apiBaseUrl);
    if (!answer.ok) return setError(answer.message);
    reps.reload();
    onChanged();
  };

  return (
    <Modal
      open={open}
      title={t('vendorRep.title')}
      onCancel={onCancel}
      dataModal="vendor-reps"
      actions={[
        // 🔒 Decision 2140: one Close where the window writes nothing (decision 3063).
        { kind: 'close', label: t('modal.close'), onAct: onCancel },
      ]}
    >
      {error ? <AlertBanner tone="error" message={error} action={{ label: t('modal.dismiss'), onAction: () => setError(null) }} /> : null}
      <p className={modalStyles.fieldLabel}>{t('vendorRep.hint')}</p>
      <ul className={styles.lines} data-vendor-reps={rows.length}>
        {rows.length === 0 && reps.state.kind !== 'loading' ? <li className={styles.description}>{t('vendorRep.none')}</li> : null}
        {rows.map((row) => (
          <li key={row.id} className={styles.line} data-vendor-rep={row.itemCategoryId} data-vendor-rep-active={row.isActive ? 'true' : 'false'}>
            <span>{line(row.categoryPath, row.isActive ? null : `(${t('vendorRep.inactive')})`)}</span>
            <Button size={Size.Small} text={[row.isActive ? t('vendorRep.remove') : t('vendorRep.restore')]} actions={[() => void toggle(row)]} />
          </li>
        ))}
      </ul>
      <Select size={Size.Large} label={t('vendorRep.category')} value={chosen} onChange={setChosen} placeholder={t('customFieldsModal.none')} options={options.map((o) => ({ value: o.id, label: o.label }))} dataAttribute={{ 'data-vendor-rep-pick': 'true' }} />
      <Button size={Size.Small} text={[t('vendorRep.add')]} actions={[() => void add()]} disabled={!chosen} />
    </Modal>
  );
}
